import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as AntApp, Input } from 'antd';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { AsyncButton, I } from '../components/kit';
import * as authApi from '../api/auth';

export default function LoginPage() {
  const { login } = useAuth();
  const { toggle } = useTheme();
  const nav = useNavigate();
  // ProtectedRoute hands over where the agent was heading (e.g. the event QR's
  // check-in link opened by the phone camera); go back there, query and all.
  // A hard redirect after a dead session can't carry router state, so it passes
  // ?next= instead (api/client.js). Same-site paths only.
  const loc = useLocation();
  const from = loc.state?.from;
  const nextParam = new URLSearchParams(loc.search).get('next') || '';
  const safeNext = /^\/(?!\/)/.test(nextParam) && !nextParam.startsWith('/login') ? nextParam : '';
  const back = from?.pathname && from.pathname !== '/login' ? `${from.pathname}${from.search || ''}` : (safeNext || '/');
  const { message } = AntApp.useApp();
  const [mode, setMode] = useState('login'); // login | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const doLogin = async () => {
    if (!email) return message.warning('Enter your email');
    await login(email, password);
    nav(back, { replace: true });
  };
  const doForgot = async () => {
    if (!email) return message.warning('Enter your email');
    await authApi.createAuthForgotPassword({ email });
    message.success('If that email exists, a reset link is on its way');
    setMode('login');
  };

  // shared sub-screen header bar (back chevron + title)
  const backHeader = (title) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
      <div onClick={() => setMode('login')} style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text)' }}>
        <I.back width={20} height={20} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
    </div>
  );

  return (
    <div className="app-frame">
      {mode === 'login' && (
        <>
          <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 5 }}>
            <div onClick={toggle} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
              <I.sun />
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 22 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#fff', borderRadius: 999, padding: '8px 16px', alignSelf: 'flex-start' }}>
                <img src="/cid_fulllogo.png" alt="CID Realtors" style={{ height: 26 }} />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>Welcome back</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Sign in to your agent account</div>
              </div>

              <label style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>Email</label>
              <Input size="large" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@cidrealtors.com" autoComplete="email" />

              <label style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>Password</label>
              <Input.Password size="large" value={password} onChange={(e) => setPassword(e.target.value)} onPressEnter={doLogin} placeholder="••••••••" autoComplete="current-password" />

              <AsyncButton type="primary" size="large" block onClick={doLogin} onError={(e) => message.error(e.message)} style={{ marginTop: 4 }}>
                Sign in
              </AsyncButton>

              <div style={{ display: 'flex', justifyContent: 'flex-start', fontSize: 13, fontWeight: 500, marginTop: 2 }}>
                <a onClick={() => setMode('forgot')} style={{ cursor: 'pointer' }}>Forgot password?</a>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--muted)', padding: '0 0 16px' }}>CID Realtors Sdn Bhd · CIDAS v{__APP_VERSION__}</div>
        </>
      )}

      {mode === 'forgot' && (
        <>
          {backHeader('Forgot password')}
          <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Enter your registered email and we’ll send a reset link.</div>
            <Input size="large" value={email} onChange={(e) => setEmail(e.target.value)} onPressEnter={doForgot} placeholder="you@cidrealtors.com" autoComplete="email" />
            <AsyncButton type="primary" size="large" block onClick={doForgot} onError={(e) => message.error(e.message)}>
              Send reset link
            </AsyncButton>
          </div>
        </>
      )}

    </div>
  );
}
